import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { expenseReimbursementService } from '../services/expenseReimbursementService';
import { timeOffRequestService } from '../services/timeOffRequestService';
import { employeeMessageService } from '../services/employeeMessageService';
import { realtimeService } from '../services/realtimeService';

/**
 * Pending HR inbox counts for managers/admins (expense receipts, time off, employee messages).
 * Refreshes on route changes, local window events, visibility/focus, polling, and realtime WebSocket.
 */
export function useHrPendingNotifications() {
  const location = useLocation();
  const { token, user, isManagerOrAdmin } = useAuth();
  const [pendingReceiptCount, setPendingReceiptCount] = useState(0);
  const [pendingTimeOffCount, setPendingTimeOffCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const manager = typeof isManagerOrAdmin === 'function' ? isManagerOrAdmin() : false;

  const loadPendingReceiptCount = useCallback(async () => {
    if (!token || !manager) {
      setPendingReceiptCount(0);
      return;
    }
    try {
      const res = await expenseReimbursementService.getPendingCount();
      setPendingReceiptCount(res.count ?? 0);
    } catch {
      /* badge is optional */
    }
  }, [token, manager]);

  const loadPendingTimeOffCount = useCallback(async () => {
    if (!token || !manager) {
      setPendingTimeOffCount(0);
      return;
    }
    try {
      const res = await timeOffRequestService.getPendingCount();
      setPendingTimeOffCount(res.count ?? 0);
    } catch {
      /* badge is optional */
    }
  }, [token, manager]);

  const loadUnreadMessageCount = useCallback(async () => {
    if (!token || !manager) {
      setUnreadMessageCount(0);
      return;
    }
    try {
      const res = await employeeMessageService.getUnreadCount();
      setUnreadMessageCount(res.count ?? 0);
    } catch {
      /* badge is optional */
    }
  }, [token, manager]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadPendingReceiptCount(),
      loadPendingTimeOffCount(),
      loadUnreadMessageCount()
    ]);
  }, [loadPendingReceiptCount, loadPendingTimeOffCount, loadUnreadMessageCount]);

  const refreshFromRealtime = useCallback(
    (event) => {
      const kind = event?.payload?.kind;
      if (kind === 'expense_reimbursement') {
        loadPendingReceiptCount();
        return;
      }
      if (kind === 'time_off') {
        loadPendingTimeOffCount();
        return;
      }
      if (kind === 'employee_message') {
        loadUnreadMessageCount();
        return;
      }
      refreshAll();
    },
    [loadPendingReceiptCount, loadPendingTimeOffCount, loadUnreadMessageCount, refreshAll]
  );

  useEffect(() => {
    refreshAll();
    if (!token || !manager) return undefined;
    const interval = setInterval(refreshAll, 60000);
    return () => clearInterval(interval);
  }, [refreshAll, token, user?.role, manager]);

  useEffect(() => {
    refreshAll();
  }, [location.pathname, refreshAll]);

  useEffect(() => {
    const onReceiptsUpdated = () => loadPendingReceiptCount();
    const onTimeOffUpdated = () => loadPendingTimeOffCount();
    const onMessagesUpdated = () => loadUnreadMessageCount();
    window.addEventListener('expense-reimbursements-updated', onReceiptsUpdated);
    window.addEventListener('time-off-requests-updated', onTimeOffUpdated);
    window.addEventListener('employee-messages-updated', onMessagesUpdated);
    return () => {
      window.removeEventListener('expense-reimbursements-updated', onReceiptsUpdated);
      window.removeEventListener('time-off-requests-updated', onTimeOffUpdated);
      window.removeEventListener('employee-messages-updated', onMessagesUpdated);
    };
  }, [loadPendingReceiptCount, loadPendingTimeOffCount, loadUnreadMessageCount]);

  // Realtime: keep a WebSocket open for managers and refresh counts when HR inbox changes.
  useEffect(() => {
    if (!token || !manager) return undefined;

    realtimeService.connect();
    const unsubscribe = realtimeService.subscribe((event) => {
      if (event?.entity === 'hr_inbox') {
        refreshFromRealtime(event);
      }
    });

    return unsubscribe;
  }, [token, manager, refreshFromRealtime]);

  // Refresh when the browser tab becomes visible again (covers missed WS events).
  useEffect(() => {
    if (!token || !manager) return undefined;

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshAll();
      }
    };
    const onFocus = () => refreshAll();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [token, manager, refreshAll]);

  const total =
    (pendingReceiptCount > 0 ? pendingReceiptCount : 0) +
    (pendingTimeOffCount > 0 ? pendingTimeOffCount : 0) +
    (unreadMessageCount > 0 ? unreadMessageCount : 0);

  return {
    manager,
    pendingReceiptCount,
    pendingTimeOffCount,
    unreadMessageCount,
    total,
    refreshAll
  };
}
